import React, { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/utils/api'
import { useCustodyParents, type CustodyParentsConfig } from '@/hooks/useCustodyParents'
import { Plus, Edit2, Trash2, Save, X, Calendar as CalendarIcon, School as SchoolIcon } from 'lucide-react'

/**
 * Phase 5b of OpenSpec change `custody-aware-transport`.
 *
 * Rules editor for a single kid. Consumes the Phase 5a API:
 *   GET    /api/transport/rules?user_id=:id
 *   POST   /api/transport/rules
 *   PUT    /api/transport/rules/:id
 *   DELETE /api/transport/rules/:id
 *   GET    /api/transport/preview?user_id=:id&from=:d&to=:d
 *
 * Scope is intentionally narrow for the first release: rule list + add/edit/
 * delete + 14-day preview. Custody-split columns, table view, date_range
 * UX, and bulk edit are deferred.
 */

export type TransportRule = {
  id: string
  user_id: string
  priority: number
  scope: 'default' | 'custody_phase' | 'day_of_week' | 'day_of_week_and_custody' | 'date_range'
  custody_phase: 'at_primary' | 'at_other' | null
  day_of_week: number | null
  date_range_start: string | null
  date_range_end: string | null
  direction: 'morning' | 'afternoon'
  transport_mode: string
  bus_number: string | null
  pickup_time: string | null
  dropoff_time: string | null
  notes: string | null
}

type PreviewDay = {
  date: string
  morning: {
    mode: string
    bus_number: string | null
    time: string | null
    source: 'override' | 'rule' | 'default'
    rule_id: string | null
    notes: string | null
  } | null
  afternoon: PreviewDay['morning']
  no_school?: boolean
  early_release?: boolean
  event_name?: string | null
}

type SchoolHours = {
  start_time: string | null
  end_time: string | null
  override_start_time: string | null
  override_end_time: string | null
  school_default_start_time: string | null
  school_default_end_time: string | null
  source: 'override' | 'school' | 'none'
  school_id: string | null
  school_name: string | null
}

// "car" intentionally omitted — ambiguous vs parent_car / drives_self / carpool.
// Legacy rows with mode === 'car' still render via modeLabel() fallthrough.
const MODES = [
  { value: 'bus',          label: 'School Bus',   icon: '🚌' },
  { value: 'parent_car',   label: 'Parent Car',   icon: '🚗' },
  { value: 'carpool',      label: 'Carpool',      icon: '🚙' },
  { value: 'friend_pickup',label: 'Friend Pickup',icon: '🙋' },
  { value: 'walk',         label: 'Walk',         icon: '🚶' },
  { value: 'bike',         label: 'Bike',         icon: '🚲' },
  { value: 'drives_self',  label: 'Drives Self',  icon: '🔑' },
]

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function modeLabel(mode: string): string {
  return MODES.find(m => m.value === mode)?.label || mode
}
function modeIcon(mode: string): string {
  return MODES.find(m => m.value === mode)?.icon || '❓'
}

function toTimeInput(v: string | null | undefined): string {
  if (!v) return ''
  return v.length === 5 ? v : v.slice(0, 5)
}

function formatTime(v: string | null | undefined): string {
  if (!v) return '—'
  const [h, m] = v.split(':')
  const hh = parseInt(h, 10)
  const ampm = hh >= 12 ? 'PM' : 'AM'
  const hr = hh > 12 ? hh - 12 : (hh === 0 ? 12 : hh)
  return `${hr}:${m} ${ampm}`
}

type Draft = Partial<TransportRule>

function emptyDraft(user_id: string): Draft {
  return {
    user_id,
    priority: 99,
    scope: 'default',
    custody_phase: null,
    day_of_week: null,
    date_range_start: null,
    date_range_end: null,
    direction: 'morning',
    transport_mode: 'bus',
    bus_number: null,
    pickup_time: null,
    dropoff_time: null,
    notes: null,
  }
}

interface Props {
  userId: string
  displayName: string
  onClose?: () => void
}

export const TransportRulesEditor: React.FC<Props> = ({ userId, displayName }) => {
  const custodyParents = useCustodyParents()
  const [rules, setRules] = useState<TransportRule[]>([])
  const [preview, setPreview] = useState<PreviewDay[]>([])
  const [schoolHours, setSchoolHours] = useState<SchoolHours | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [rulesResp, previewResp, hoursResp] = await Promise.all([
        apiClient.get(`/api/transport/rules?user_id=${userId}`),
        (() => {
          const today = new Date()
          const end = new Date()
          end.setDate(today.getDate() + 14)
          const iso = (d: Date) => d.toISOString().slice(0, 10)
          return apiClient.get(`/api/transport/preview?user_id=${userId}&from=${iso(today)}&to=${iso(end)}`)
        })(),
        apiClient.get(`/api/transport/school-hours?user_id=${userId}`).catch(() => null),
      ])
      setRules(rulesResp.rules || [])
      setPreview(previewResp.days || [])
      setSchoolHours(hoursResp as SchoolHours | null)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load rules')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => { load() }, [load])

  const startCreate = () => {
    setEditingId(null)
    setDraft(emptyDraft(userId))
  }
  const startEdit = (rule: TransportRule) => {
    setEditingId(rule.id)
    setDraft({ ...rule })
  }
  const cancel = () => {
    setEditingId(null)
    setDraft(null)
  }

  const save = async () => {
    if (!draft) return
    setError(null)
    const payload = {
      ...draft,
      // time inputs come back as "HH:MM" — backend accepts that shape
      pickup_time: draft.pickup_time || null,
      dropoff_time: draft.dropoff_time || null,
    }
    try {
      if (editingId) {
        await apiClient.put(`/api/transport/rules/${editingId}`, payload)
      } else {
        await apiClient.post('/api/transport/rules', payload)
      }
      cancel()
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Save failed')
    }
  }

  const remove = async (id: string) => {
    if (!confirm('Delete this rule? Can be recreated if needed.')) return
    setError(null)
    try {
      await apiClient.delete(`/api/transport/rules/${id}`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Delete failed')
    }
  }

  const isBus = draft?.transport_mode === 'bus'

  // Quick-add weekday default state (separate draft; upserts 2 rules in one submit).
  // Targets priority-99 default rules — the "main slot" for each direction.
  const [quickDraft, setQuickDraft] = useState<null | {
    mode: string
    bus_number: string
    morning_time: string
    afternoon_time: string
  }>(null)

  const existingMorningDefault = rules.find(r => r.scope === 'default' && r.priority === 99 && r.direction === 'morning')
  const existingAfternoonDefault = rules.find(r => r.scope === 'default' && r.priority === 99 && r.direction === 'afternoon')
  const hasExistingDefault = !!(existingMorningDefault || existingAfternoonDefault)

  const startQuickAdd = () => {
    // Pre-populate from existing default rules so "Set weekday default" behaves
    // like "Edit weekday default" when one already exists.
    const seed = existingMorningDefault || existingAfternoonDefault
    setQuickDraft({
      mode: seed?.transport_mode || 'bus',
      bus_number: seed?.bus_number || '',
      morning_time: toTimeInput(existingMorningDefault?.pickup_time),
      afternoon_time: toTimeInput(existingAfternoonDefault?.dropoff_time),
    })
  }
  const cancelQuick = () => setQuickDraft(null)
  const saveQuick = async () => {
    if (!quickDraft) return
    setError(null)
    const base = {
      user_id: userId,
      priority: 99,
      scope: 'default' as const,
      custody_phase: null,
      day_of_week: null,
      date_range_start: null,
      date_range_end: null,
      transport_mode: quickDraft.mode,
      bus_number: quickDraft.bus_number || null,
      notes: null,
    }
    const morningPayload = {
      ...base,
      direction: 'morning',
      pickup_time: quickDraft.morning_time || null,
      dropoff_time: null,
    }
    const afternoonPayload = {
      ...base,
      direction: 'afternoon',
      pickup_time: null,
      dropoff_time: quickDraft.afternoon_time || null,
    }
    try {
      await Promise.all([
        existingMorningDefault
          ? apiClient.put(`/api/transport/rules/${existingMorningDefault.id}`, morningPayload)
          : apiClient.post('/api/transport/rules', morningPayload),
        existingAfternoonDefault
          ? apiClient.put(`/api/transport/rules/${existingAfternoonDefault.id}`, afternoonPayload)
          : apiClient.post('/api/transport/rules', afternoonPayload),
      ])
      cancelQuick()
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Save failed')
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-5 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-gray-900">Transport Rules — {displayName}</h3>
        {!draft && !quickDraft && (
          <div className="flex items-center gap-2">
            <button onClick={startQuickAdd} className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-1" /> {hasExistingDefault ? 'Edit weekday default' : 'Set weekday default'}
            </button>
            <button onClick={startCreate} className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-1" /> Add rule
            </button>
          </div>
        )}
      </div>

      {/* Quick-add weekday default — creates morning + afternoon default rules in one submit */}
      {quickDraft && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4 space-y-3">
          <div className="flex items-baseline justify-between gap-2">
            <h4 className="text-sm font-semibold text-emerald-900">
              {hasExistingDefault ? 'Edit' : 'Set'} weekday default — {displayName}
            </h4>
            <span className="text-xs text-emerald-800">
              {hasExistingDefault ? 'Updates your existing default rules.' : 'Creates one morning + one afternoon default rule.'}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="text-xs text-gray-700">
              Mode
              <select
                value={quickDraft.mode}
                onChange={(e) => setQuickDraft({ ...quickDraft, mode: e.target.value })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm"
              >
                {MODES.map(m => <option key={m.value} value={m.value}>{m.icon} {m.label}</option>)}
              </select>
            </label>
            {quickDraft.mode === 'bus' && (
              <label className="text-xs text-gray-700">
                Bus number
                <input type="text"
                  value={quickDraft.bus_number}
                  onChange={(e) => setQuickDraft({ ...quickDraft, bus_number: e.target.value })}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"
                  placeholder="e.g. 123" />
              </label>
            )}
            <label className="text-xs text-gray-700">
              Morning time
              <input type="time"
                value={quickDraft.morning_time}
                onChange={(e) => setQuickDraft({ ...quickDraft, morning_time: e.target.value })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm" />
            </label>
            <label className="text-xs text-gray-700">
              Afternoon time
              <input type="time"
                value={quickDraft.afternoon_time}
                onChange={(e) => setQuickDraft({ ...quickDraft, afternoon_time: e.target.value })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm" />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveQuick} className="inline-flex items-center px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-1" /> Create both rules
            </button>
            <button onClick={cancelQuick} className="inline-flex items-center px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
              <X className="w-4 h-4 mr-1" /> Cancel
            </button>
          </div>
        </div>
      )}

      {error && <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded">{error}</div>}
      {loading && <div className="text-gray-500 text-sm">Loading…</div>}

      {schoolHours && (
        <SchoolHoursCard
          userId={userId}
          hours={schoolHours}
          onUpdated={(h) => setSchoolHours(h)}
          onError={setError}
        />
      )}

      {/* Draft form */}
      {draft && (
        <div className="border border-blue-200 bg-blue-50 rounded-md p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="text-xs text-gray-700">
              Direction
              <select
                value={draft.direction || 'morning'}
                onChange={(e) => setDraft({ ...draft, direction: e.target.value as 'morning' | 'afternoon' })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm"
              >
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
              </select>
            </label>

            <label className="text-xs text-gray-700">
              Scope
              <select
                value={draft.scope || 'default'}
                onChange={(e) => setDraft({
                  ...draft,
                  scope: e.target.value as TransportRule['scope'],
                  // reset scope-specific fields when scope changes
                  custody_phase: null, day_of_week: null,
                  date_range_start: null, date_range_end: null,
                })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm"
              >
                <option value="default">Default (always)</option>
                <option value="day_of_week">Day of week</option>
                <option value="custody_phase">Custody (at {custodyParents.primary}'s / at {custodyParents.other}'s)</option>
                <option value="day_of_week_and_custody">Day + custody</option>
                <option value="date_range">Date range</option>
              </select>
            </label>

            <label className="text-xs text-gray-700">
              Mode
              <select
                value={draft.transport_mode || 'bus'}
                onChange={(e) => setDraft({ ...draft, transport_mode: e.target.value })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm"
              >
                {MODES.map(m => <option key={m.value} value={m.value}>{m.icon} {m.label}</option>)}
              </select>
            </label>

            <label className="text-xs text-gray-700">
              Priority
              <input
                type="number" min={1} max={999}
                value={draft.priority ?? 99}
                onChange={(e) => setDraft({ ...draft, priority: parseInt(e.target.value || '99', 10) })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm"
              />
            </label>
          </div>

          {/* Scope-specific */}
          {(draft.scope === 'custody_phase' || draft.scope === 'day_of_week_and_custody') && (
            <label className="block text-xs text-gray-700">
              Custody phase
              <select
                value={draft.custody_phase || ''}
                onChange={(e) => setDraft({ ...draft, custody_phase: (e.target.value || null) as any })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm"
              >
                <option value="">— select —</option>
                <option value="at_primary">At {custodyParents.primary}'s</option>
                <option value="at_other">At {custodyParents.other}'s</option>
              </select>
            </label>
          )}
          {(draft.scope === 'day_of_week' || draft.scope === 'day_of_week_and_custody') && (
            <label className="block text-xs text-gray-700">
              Day of week
              <select
                value={draft.day_of_week ?? ''}
                onChange={(e) => setDraft({ ...draft, day_of_week: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                className="mt-1 w-full px-2 py-1 border rounded text-sm"
              >
                <option value="">— select —</option>
                {DOW_LABELS.map((lbl, i) => <option key={i} value={i}>{lbl}</option>)}
              </select>
            </label>
          )}
          {draft.scope === 'date_range' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-gray-700">
                Start
                <input type="date"
                  value={draft.date_range_start || ''}
                  onChange={(e) => setDraft({ ...draft, date_range_start: e.target.value || null })}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"/>
              </label>
              <label className="text-xs text-gray-700">
                End
                <input type="date"
                  value={draft.date_range_end || ''}
                  onChange={(e) => setDraft({ ...draft, date_range_end: e.target.value || null })}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"/>
              </label>
            </div>
          )}

          {/* Mode-specific */}
          {isBus && (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-gray-700">
                Bus number
                <input type="text"
                  value={draft.bus_number || ''}
                  onChange={(e) => setDraft({ ...draft, bus_number: e.target.value || null })}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"
                  placeholder="e.g. 123"/>
              </label>
            </div>
          )}
          {/* Time field is always shown — leave it blank for modes that don't need one. */}
          <div className="grid grid-cols-2 gap-3">
            {draft.direction === 'morning' ? (
              <label className="text-xs text-gray-700">
                Morning time
                <input type="time"
                  value={toTimeInput(draft.pickup_time)}
                  onChange={(e) => setDraft({ ...draft, pickup_time: e.target.value || null })}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"/>
              </label>
            ) : (
              <label className="text-xs text-gray-700">
                Afternoon time
                <input type="time"
                  value={toTimeInput(draft.dropoff_time)}
                  onChange={(e) => setDraft({ ...draft, dropoff_time: e.target.value || null })}
                  className="mt-1 w-full px-2 py-1 border rounded text-sm"/>
              </label>
            )}
          </div>

          <label className="block text-xs text-gray-700">
            Notes
            <input type="text"
              value={draft.notes || ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value || null })}
              className="mt-1 w-full px-2 py-1 border rounded text-sm"
              placeholder="Optional" />
          </label>

          <div className="flex items-center gap-2">
            <button onClick={save} className="inline-flex items-center px-3 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700">
              <Save className="w-4 h-4 mr-1" /> {editingId ? 'Save changes' : 'Create rule'}
            </button>
            <button onClick={cancel} className="inline-flex items-center px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
              <X className="w-4 h-4 mr-1" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Rules list */}
      {!loading && rules.length === 0 && !draft && (
        <p className="text-gray-500 text-sm">No rules yet. Click "Add rule" to create one.</p>
      )}
      {rules.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Direction</th>
                <th className="px-3 py-2 text-left">Scope</th>
                <th className="px-3 py-2 text-left">Mode</th>
                <th className="px-3 py-2 text-left">Time</th>
                <th className="px-3 py-2 text-left">Priority</th>
                <th className="px-3 py-2 text-left">Notes</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedRules(rules).map(r => (
                <tr key={r.id} className={r.priority >= 900 ? 'opacity-60' : ''}>
                  <td className="px-3 py-2 capitalize">{r.direction}</td>
                  <td className="px-3 py-2">{scopeLabel(r, custodyParents)}</td>
                  <td className="px-3 py-2">{modeIcon(r.transport_mode)} {modeLabel(r.transport_mode)}{r.bus_number ? ` #${r.bus_number}` : ''}</td>
                  <td className="px-3 py-2">{formatTime(r.direction === 'morning' ? r.pickup_time : r.dropoff_time)}</td>
                  <td className="px-3 py-2">{r.priority}{r.priority >= 900 ? ' (dormant)' : ''}</td>
                  <td className="px-3 py-2 text-gray-500">{r.notes}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => startEdit(r)} className="p-1 text-gray-600 hover:text-blue-600"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(r.id)} className="p-1 text-gray-600 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 14-day preview — weekdays only */}
      {preview.length > 0 && (
        <div className="border-t pt-4 mt-2">
          <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
            <CalendarIcon className="w-4 h-4 mr-1" /> Next 14 weekdays (preview)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            {weekdaysOnly(preview).map(day => {
              const d = new Date(day.date + 'T00:00:00')
              const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

              if (day.no_school) {
                return (
                  <div key={day.date} className="flex items-center justify-between rounded border border-gray-200 bg-gray-100 px-2 py-1">
                    <span className="font-medium text-gray-500 line-through">{label}</span>
                    <span className="text-gray-600 text-xs">No school{day.event_name ? ` · ${day.event_name}` : ''}</span>
                  </div>
                )
              }

              return (
                <div key={day.date} className={'flex items-center justify-between rounded border px-2 py-1 ' + (day.early_release ? 'border-amber-200 bg-amber-50' : 'border-gray-100')}>
                  <span className="font-medium text-gray-700">
                    {label}
                    {day.early_release && day.event_name && <span className="ml-1 text-[10px] text-amber-700">· {day.event_name}</span>}
                  </span>
                  <span className="text-gray-600">
                    AM: {day.morning ? `${modeIcon(day.morning.mode)} ${formatTime(day.morning.time)}` : <span className="text-amber-600">unset</span>}
                    <span className="mx-2 text-gray-300">|</span>
                    PM: {day.afternoon ? `${modeIcon(day.afternoon.mode)} ${formatTime(day.afternoon.time)}` : <span className="text-amber-600">unset</span>}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// Morning before afternoon; within a direction, priority ascending (lowest = wins).
function sortedRules(rules: TransportRule[]): TransportRule[] {
  const dirOrder: Record<string, number> = { morning: 0, afternoon: 1 }
  return rules.slice().sort((a, b) => {
    const d = (dirOrder[a.direction] ?? 2) - (dirOrder[b.direction] ?? 2)
    if (d !== 0) return d
    return (a.priority ?? 99) - (b.priority ?? 99)
  })
}

// Drop Sat/Sun from the 14-day preview — weekend rows were confusing for parents
// because the resolver renders them as active commute days. If a parent has a
// rule that legitimately applies on weekends, they can use Add Rule + day_of_week.
function weekdaysOnly(days: PreviewDay[]): PreviewDay[] {
  return days.filter(d => {
    const dow = new Date(d.date + 'T00:00:00').getDay()
    return dow !== 0 && dow !== 6
  })
}

function scopeLabel(r: TransportRule, parents: CustodyParentsConfig): string {
  switch (r.scope) {
    case 'default': return 'Default'
    case 'custody_phase':
      return r.custody_phase === 'at_primary' ? `At ${parents.primary}'s` : r.custody_phase === 'at_other' ? `At ${parents.other}'s` : 'Custody'
    case 'day_of_week':
      return r.day_of_week == null ? 'Day of week' : DOW_LABELS[r.day_of_week]
    case 'day_of_week_and_custody': {
      const dow = r.day_of_week == null ? '' : DOW_LABELS[r.day_of_week]
      const phase = r.custody_phase === 'at_primary' ? `@${parents.primary}` : r.custody_phase === 'at_other' ? `@${parents.other}` : ''
      return [dow, phase].filter(Boolean).join(' ')
    }
    case 'date_range':
      return `${r.date_range_start || '?'} → ${r.date_range_end || '?'}`
    default: return r.scope
  }
}

// ── School hours card ─────────────────────────────────────────────────────
// Small summary card above the rules: displays effective school start/end
// time, where they came from (per-kid override, school default, or neither),
// and an inline edit form that writes to platform_user_school_info.

const SchoolHoursCard: React.FC<{
  userId: string
  hours: SchoolHours
  onUpdated: (h: SchoolHours) => void
  onError: (msg: string) => void
}> = ({ userId, hours, onUpdated, onError }) => {
  const [editing, setEditing] = useState(false)
  const [startTime, setStartTime] = useState(toTimeInput(hours.override_start_time ?? hours.start_time))
  const [endTime, setEndTime] = useState(toTimeInput(hours.override_end_time ?? hours.end_time))
  const [saving, setSaving] = useState(false)

  const openEdit = () => {
    // Re-seed from current props in case parent reloaded.
    setStartTime(toTimeInput(hours.override_start_time ?? hours.start_time))
    setEndTime(toTimeInput(hours.override_end_time ?? hours.end_time))
    setEditing(true)
  }

  const save = async () => {
    setSaving(true)
    try {
      const resp: any = await apiClient.put('/api/transport/school-hours', {
        user_id: userId,
        override_start_time: startTime || null,
        override_end_time: endTime || null,
      })
      onUpdated(resp as SchoolHours)
      setEditing(false)
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const clearOverride = async () => {
    setSaving(true)
    try {
      const resp: any = await apiClient.put('/api/transport/school-hours', {
        user_id: userId,
        override_start_time: null,
        override_end_time: null,
      })
      onUpdated(resp as SchoolHours)
      setEditing(false)
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || 'Clear failed')
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div className="border border-sky-200 bg-sky-50 rounded-md p-4 space-y-3">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="text-sm font-semibold text-sky-900 flex items-center">
            <SchoolIcon className="w-4 h-4 mr-1" /> School hours
          </h4>
          <span className="text-xs text-sky-800">
            Saved as a per-kid override. Leave blank to fall back to the school default.
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-gray-700">
            Start time
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="mt-1 w-full px-2 py-1 border rounded text-sm" />
          </label>
          <label className="text-xs text-gray-700">
            End time
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
              className="mt-1 w-full px-2 py-1 border rounded text-sm" />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </button>
          {hours.source === 'override' && (
            <button onClick={clearOverride} disabled={saving}
              className="inline-flex items-center px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Clear override
            </button>
          )}
          <button onClick={() => setEditing(false)} disabled={saving}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <X className="w-4 h-4 mr-1" /> Cancel
          </button>
        </div>
      </div>
    )
  }

  const displayStart = formatTime(hours.start_time)
  const displayEnd = formatTime(hours.end_time)
  const hasAnyHours = hours.start_time != null || hours.end_time != null

  return (
    <div className="border border-gray-200 rounded-md px-4 py-3 flex items-center justify-between bg-gray-50/60">
      <div className="flex items-center gap-2 flex-wrap">
        <SchoolIcon className="w-4 h-4 text-gray-600 shrink-0" />
        <span className="text-sm font-medium text-gray-800">School hours:</span>
        {hasAnyHours ? (
          <span className="text-sm text-gray-800">{displayStart} – {displayEnd}</span>
        ) : (
          <span className="text-sm text-gray-500 italic">Not set</span>
        )}
        {hours.school_name && (
          <span className="text-xs text-gray-500">· {hours.school_name}</span>
        )}
        <span className={
          'text-xs px-1.5 py-0.5 rounded ' +
          (hours.source === 'override' ? 'bg-amber-100 text-amber-800' :
           hours.source === 'school' ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-500')
        }>
          {hours.source === 'override' ? 'custom' : hours.source === 'school' ? 'school default' : 'none'}
        </span>
      </div>
      <button onClick={openEdit} className="p-1 text-gray-500 hover:text-blue-600" title="Edit school hours">
        <Edit2 className="w-4 h-4" />
      </button>
    </div>
  )
}

export default TransportRulesEditor
